import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageIcon, Package } from 'lucide-react';
import Button from './Button';
import ProductCategoryOverview from './ProductCategoryOverview';
import PurchaseRequestModal from './PurchaseRequestModal';
import ContactStrategistModal from './ContactStrategistModal';
import PurchaseCreditsModal from './PurchaseCreditsModal';
import OrderHistory from './OrderHistory';
import GatheringInfoToast from './GatheringInfoToast';
import { useCaps } from '../contexts/CapabilitiesContext';
import TripleDiamondHero from './TripleDiamondHero';
import { queryKeys } from '../queryKeys';
import {
  fetchMarketplaceCatalog,
  formatMarketplaceProductPricing,
  toPurchaseProduct,
  type PurchaseProduct,
} from '../utils/marketplaceProducts';

interface MarketplaceProps {
  onCategorySelect?: (categoryId: number) => void;
  selectedCurrency?: string;
  initialOverviewCategoryId?: number | null;
  initialProductId?: number | null;
  onInitialSelectionConsumed?: () => void;
}

interface ProductCategory {
  id: number;
  name: string;
  short_description: string;
  category_type: string;
  category_type_id: number;
  category_type_description?: string;
  category_type_color?: string;
  long_description: string;
  workspace_key?: string;
  image?: string | null;
  image_url?: string | null;
  created_at: string;
}

interface CategoryType {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  display_order: number;
  is_active: boolean;
}

interface CoreOutput {
  name: string;
  text?: string;
  path: string;
  image_url?: string;
}

interface Product {
  id: number;
  name: string;
  product_category: number;
  credits: number;
  duration_in_days: number;
  short_description: string;
  long_description: string;
  price: string;
  currency: string;
  is_free: boolean;
  service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  product_category_workspace_key?: string;
  core_outputs_json?: CoreOutput[];
  created_at: string;
}

const DEFAULT_COLORS: Record<string, string> = {
  'Innovation Platform': '#C3D74F',
  'Innovation Recipe': '#807CB1',
  'Product Idea': '#43C8F5',
};

function pickLowestCreditsProduct(products: Product[]): Product | null {
  if (products.length === 0) return null;
  return products.reduce((best, product) => {
    if (product.credits < best.credits) return product;
    if (product.credits === best.credits && product.duration_in_days < best.duration_in_days) {
      return product;
    }
    return best;
  });
}

function getDisplayProduct(
  products: Product[],
  selectedProductId: number | null
): Product | null {
  if (products.length === 0) return null;
  if (products.length === 1) return products[0];
  if (selectedProductId != null) {
    return products.find((product) => product.id === selectedProductId) ?? null;
  }
  return pickLowestCreditsProduct(products);
}

const Marketplace: React.FC<MarketplaceProps> = ({
  selectedCurrency = 'CHF',
  initialOverviewCategoryId = null,
  initialProductId = null,
  onInitialSelectionConsumed,
}) => {
  const [selectedProducts, setSelectedProducts] = useState<Record<number, number | null>>({});
  const {
    data: catalog,
    isPending: isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.marketplace.catalog(),
    queryFn: fetchMarketplaceCatalog,
    staleTime: 10 * 60 * 1000,
  });
  const categoryTypes = (catalog?.types || []) as CategoryType[];
  const categories = (catalog?.categories || []) as ProductCategory[];
  const products = (catalog?.products || []) as Product[];
  const error = isError ? 'Failed to load marketplace' : null;
  const [overviewCategoryId, setOverviewCategoryId] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [strategistOpen, setStrategistOpen] = useState(false);
  const [strategistSuccessMessage, setStrategistSuccessMessage] = useState<string | null>(null);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditsSuccessMessage, setCreditsSuccessMessage] = useState<string | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseProduct, setPurchaseProduct] = useState<PurchaseProduct | null>(null);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);

  const productsByCategory = useMemo(() => {
    const grouped: Record<number, Product[]> = {};
    products.forEach((product) => {
      if (!grouped[product.product_category]) {
        grouped[product.product_category] = [];
      }
      grouped[product.product_category].push(product);
    });
    return grouped;
  }, [products]);

  useEffect(() => {
    if (initialOverviewCategoryId == null) return;
    setOverviewCategoryId(initialOverviewCategoryId);
    setPendingProductId(initialProductId ?? null);
    onInitialSelectionConsumed?.();
  }, [initialOverviewCategoryId, initialProductId, onInitialSelectionConsumed]);

  useEffect(() => {
    if (overviewCategoryId == null) return;
    const scrollToTop = () => {
      const main = document.querySelector('main.flex-1.overflow-auto');
      if (main instanceof HTMLElement) {
        main.scrollTo({ top: 0, left: 0 });
        return;
      }
      window.scrollTo({ top: 0, left: 0 });
    };
    requestAnimationFrame(scrollToTop);
  }, [overviewCategoryId]);

  const sortedTypes = useMemo(
    () => [...categoryTypes].sort((a, b) => a.display_order - b.display_order || a.id - b.id),
    [categoryTypes]
  );

  const getCategoriesByType = (categoryTypeId: number) =>
    categories
      .filter((category) => category.category_type_id === categoryTypeId)
      .sort((a, b) => a.id - b.id);

  const getCategoryColor = (categoryType: CategoryType | undefined, category?: ProductCategory) =>
    category?.category_type_color ||
    categoryType?.color ||
    DEFAULT_COLORS[categoryType?.name || ''] ||
    '#C3D74F';

  const overviewCategory = useMemo(
    () => categories.find((category) => category.id === overviewCategoryId) ?? null,
    [categories, overviewCategoryId]
  );

  const overviewCategoryType = useMemo(
    () =>
      overviewCategory
        ? categoryTypes.find((type) => type.id === overviewCategory.category_type_id)
        : undefined,
    [categoryTypes, overviewCategory]
  );

  const relatedCategories = useMemo(() => {
    if (!overviewCategory) return [];
    return categories
      .filter(
        (category) =>
          category.category_type_id === overviewCategory.category_type_id &&
          category.id !== overviewCategory.id
      )
      .sort((a, b) => a.id - b.id);
  }, [categories, overviewCategory]);

  const getRelatedCategoryColor = (category: {
    id: number;
    category_type_id: number;
  }) => {
    const fullCategory = categories.find((item) => item.id === category.id);
    const categoryType = categoryTypes.find((type) => type.id === category.category_type_id);
    return getCategoryColor(categoryType, fullCategory);
  };

  const { can } = useCaps();
  const canAiGenerate = can('ai_generate');
  const canMarketplaceOrder = can('marketplace_order');
  const canViewOrderHistory =
    can('marketplace_order_history_org') ||
    can('marketplace_order_history_team') ||
    can('marketplace_order_history_personal');

  const isPurchaseBlocked = (product: { service_type?: string } | null | undefined) => {
    if (!product) return false;
    if (product.service_type === 'AI' && !canAiGenerate) return true;
    if (
      (product.service_type === 'Catalyx' || product.service_type === 'AI Off-Platform') &&
      !canMarketplaceOrder
    ) return true;
    return false;
  };

  const purchaseBlockedTitle = (product: { service_type?: string } | null | undefined) => {
    if (product?.service_type === 'AI' && !canAiGenerate) {
      return 'You do not have permission to order AI products';
    }
    if (
      (product?.service_type === 'Catalyx' || product?.service_type === 'AI Off-Platform') &&
      !canMarketplaceOrder
    ) {
      return 'You do not have permission to order Catalyx marketplace products';
    }
    return undefined;
  };

  const openPurchaseModal = (product: PurchaseProduct) => {
    setPurchaseProduct(product);
    setPurchaseOpen(true);
  };

  const closePurchaseModal = () => {
    setPurchaseOpen(false);
    setPurchaseProduct(null);
  };

  const openHistory = () => {
    if (!canViewOrderHistory) return;
    closePurchaseModal();
    setHistoryOpen(true);
  };

  const handleProductSelect = (categoryId: number, productId: string) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [categoryId]: productId ? Number(productId) : null,
    }));
  };

  const renderCategoryCard = (category: ProductCategory, categoryColor: string) => {
    const categoryProducts = productsByCategory[category.id] ?? [];
    const hasMultipleProducts = categoryProducts.length > 1;
    const selectedProductId = selectedProducts[category.id] ?? null;
    const displayProduct = getDisplayProduct(categoryProducts, selectedProductId);
    const purchaseBlocked = isPurchaseBlocked(displayProduct);
    const needsProductPick = hasMultipleProducts && selectedProductId == null;
    const purchaseDisabled = needsProductPick;

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-card-interactive="true"]')) {
        return;
      }
      setOverviewCategoryId(category.id);
    };

    return (
      <div
        key={category.id}
        className="w-[264px] flex-shrink-0 cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="w-[264px] h-[209px] rounded-t-lg border border-[#E8E8E8] bg-[#F9F9F9] overflow-hidden">
          {category.image_url ? (
            <img
              src={category.image_url}
              alt={category.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-300" strokeWidth={1.5} />
            </div>
          )}
        </div>

        <div className="w-[262px] mx-auto h-2" style={{ backgroundColor: categoryColor }} />

        <div className="w-[264px] min-h-[346px] rounded-b-lg border border-[#E8E8E8] border-t-0 bg-white px-3 py-3 flex flex-col">
          <h3 className="text-[18px] font-bold leading-none text-[#0C0B0B] mb-2">
            {category.name}
          </h3>
          <p className="text-[14px] font-normal leading-normal text-[#626262] flex-1">
            {category.short_description}
          </p>

          <div className="mt-auto flex flex-col gap-4 pt-4">
            {hasMultipleProducts && (
              <select
                data-card-interactive="true"
                value={selectedProductId ?? ''}
                onChange={(event) => handleProductSelect(category.id, event.target.value)}
                className="w-[239px] h-8 rounded-lg border border-[#E8E8E8] bg-white px-3 text-[14px] text-[#626262] focus:outline-none focus:ring-1 focus:ring-[#674fa3]"
              >
                <option value="" disabled hidden>
                  Select an option
                </option>
                {categoryProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            )}

            {displayProduct && (
              <p className="text-[14px] font-bold leading-none text-[#6750A4]">
                {formatMarketplaceProductPricing(displayProduct)}
              </p>
            )}

            <div
              data-card-interactive="true"
              title={purchaseBlockedTitle(displayProduct)}
            >
              <Button
                label="Purchase"
                variant="Round"
                size="Small"
                state={purchaseDisabled ? 'Disabled' : 'Enabled'}
                className="w-[239px]"
                disabled={purchaseDisabled}
                onClick={() => {
                  if (!purchaseDisabled && displayProduct) {
                    openPurchaseModal(
                      toPurchaseProduct(displayProduct, { workspaceKey: category.workspace_key })
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#674fa3] mx-auto" />
          <p className="mt-4 text-[#626262]">Loading marketplace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-6">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-[#626262] text-lg">No categories available</p>
        </div>
      </div>
    );
  }

  let mainContent: React.ReactNode;

  if (historyOpen && canViewOrderHistory) {
    mainContent = (
      <OrderHistory
        onBack={() => setHistoryOpen(false)}
        onContactStrategist={() => setStrategistOpen(true)}
      />
    );
  } else if (overviewCategory) {
    mainContent = (
      <ProductCategoryOverview
        category={overviewCategory}
        products={productsByCategory[overviewCategory.id] ?? []}
        relatedCategories={relatedCategories}
        productsByCategory={productsByCategory}
        color={getCategoryColor(overviewCategoryType, overviewCategory)}
        getRelatedCategoryColor={getRelatedCategoryColor}
        onBack={() => setOverviewCategoryId(null)}
        onViewOrderHistory={canViewOrderHistory ? openHistory : undefined}
        onPurchase={(product) => {
          openPurchaseModal(
            toPurchaseProduct(product, { workspaceKey: overviewCategory.workspace_key })
          );
        }}
        canAiGenerate={canAiGenerate}
        canMarketplaceOrder={canMarketplaceOrder}
        onSelectCategory={setOverviewCategoryId}
        initialProductId={pendingProductId}
      />
    );
  } else {
    mainContent = (
      <div className="w-full min-h-full bg-white font-[Inter,sans-serif]">
        <div className="h-[99px] bg-[#F9F9F9] border-b border-[#E8E8E8] px-4 flex items-center gap-6">
          <div className="min-w-0">
            <div className="flex items-baseline gap-3">
              <h1 className="text-[24px] font-bold leading-none text-[#0C0B0B]">Marketplace</h1>
              <span className="text-[14px] font-normal leading-none text-black">
                IDEAS. BUILT RIGHT™
              </span>
            </div>
            <p className="mt-3 text-[14px] font-normal leading-none text-[#626262]">
              Buy the inspiration or the proof that you are moving in exactly the direction the
              consumer wants. Module by module, in days.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {canViewOrderHistory && (
              <button
                type="button"
                onClick={openHistory}
                className="px-5 py-2 rounded-full border border-[#3da3d4] text-[#3da3d4] text-[14px] font-medium hover:bg-[#3da3d4]/5 transition-colors"
              >
                Order history
              </button>
            )}
            <button
              type="button"
              onClick={() => setCreditsOpen(true)}
              className="px-5 py-2 rounded-full bg-[#674fa3] text-white text-[14px] font-medium hover:bg-[#5a4490] transition-colors"
            >
              Purchase credits
            </button>
          </div>
        </div>

        <div className="px-4 pt-4">
          <TripleDiamondHero
            onLearnMore={(categoryName) => {
              const target = categoryName.trim().toLowerCase();
              const aliases = target === 'platform identifier' ? ['tension identifier'] : [];
              const match = categories.find((category) => {
                const name = category.name.trim().toLowerCase();
                return name === target || aliases.includes(name);
              });
              if (match) setOverviewCategoryId(match.id);
            }}
          />
        </div>

        <div className="px-4 my-[15px]">
          <p className="text-[14px] font-normal leading-none text-[#626262] text-left">
            Need help selecting the right service?{' '}
            <button
              type="button"
              onClick={() => setStrategistOpen(true)}
              className="underline text-[#3da3d4] hover:text-[#2d8ab8] transition-colors"
            >
              Contact a Catalyx Strategist
            </button>
          </p>
        </div>

        <div className="px-4 pb-10">
          <div className="flex flex-wrap items-start gap-6 max-w-[1128px]">
            {sortedTypes.map((categoryType) => {
              const typeCategories = getCategoriesByType(categoryType.id);
              if (typeCategories.length === 0) return null;

              return (
                <div key={categoryType.id} className="flex flex-col gap-4">
                  <div>
                    <h2 className="text-[24px] font-bold leading-none text-[#0C0B0B] mb-2">
                      {categoryType.name}
                    </h2>
                    {categoryType.description && (
                      <p className="text-[14px] font-normal leading-none text-[#626262] max-w-[727px]">
                        {categoryType.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-6">
                    {typeCategories.map((category) =>
                      renderCategoryCard(category, getCategoryColor(categoryType, category))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {mainContent}
      <PurchaseRequestModal
        open={purchaseOpen}
        product={purchaseProduct}
        selectedCurrency={selectedCurrency}
        onClose={closePurchaseModal}
        onViewOrderHistory={canViewOrderHistory ? openHistory : undefined}
      />
      <PurchaseCreditsModal
        open={creditsOpen}
        selectedCurrency={selectedCurrency}
        onClose={() => setCreditsOpen(false)}
        onSuccess={() =>
          setCreditsSuccessMessage('Your credit purchase request has been sent.')
        }
        onEnquireEnterprise={() => {
          setCreditsOpen(false);
          setStrategistOpen(true);
        }}
      />
      <ContactStrategistModal
        open={strategistOpen}
        onClose={() => setStrategistOpen(false)}
        onSuccess={() =>
          setStrategistSuccessMessage('You have successfully contacted a Catalyx strategist.')
        }
      />
      <GatheringInfoToast
        message={creditsSuccessMessage || strategistSuccessMessage}
        onClear={() => {
          setCreditsSuccessMessage(null);
          setStrategistSuccessMessage(null);
        }}
        variant="success"
      />
    </>
  );
};

export default Marketplace;
